/**
 * Guided Excel Import Wizard — used by every module that accepts bulk data
 * upload (Engineering DCI, Procurement MCI, F&C, Pre-Comm, Commissioning).
 *
 * 4-step workflow:
 *   1. Template  — download a pre-formatted xlsx (from the module's backend
 *                  endpoint) with sample rows + column-level hints.
 *   2. Upload    — pick a file, parse it in-browser with SheetJS, auto-map
 *                  the source columns onto the canonical field keys using
 *                  header-string similarity, and let the user override.
 *   3. Validate  — run field-level rules (required / enum / regex / min / max
 *                  / date-parseable / duplicate id) and surface every issue in
 *                  a table.  User can download `errors.xlsx` where the
 *                  offending cells are highlighted red and the message sits
 *                  in a note column so they can fix in Excel and re-upload.
 *   4. Confirm   — POST the file to the module's import endpoint. Show the
 *                  server-side stats (created / updated / skipped) plus any
 *                  new server-side errors.
 *
 * Config-driven: every module supplies its own `fields`, `templateEndpoint`,
 * `importEndpoint` and `sampleRows`.  The wizard is otherwise identical.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle,
  ChevronRight, ChevronLeft, FileDown, Loader2, RefreshCw,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import api from '@/lib/api';

// ─── Similarity heuristic to auto-map source headers → canonical keys ──
const _norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const _similarity = (a, b) => {
  const na = _norm(a), nb = _norm(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.85;
  // Jaccard on character trigrams
  const trig = (s) => new Set([...Array(Math.max(0, s.length - 2))].map((_, i) => s.slice(i, i + 3)));
  const A = trig(na), B = trig(nb);
  if (!A.size || !B.size) return 0;
  const inter = [...A].filter((x) => B.has(x)).length;
  const uni = new Set([...A, ...B]).size;
  return uni ? inter / uni : 0;
};

const _autoMap = (sourceCols, fields) => {
  const map = {};
  sourceCols.forEach((sc) => {
    let best = null, bestScore = 0.4;    // 0.4 minimum threshold
    fields.forEach((f) => {
      const candidates = [f.label, f.key, ...(f.aliases || [])];
      const s = Math.max(...candidates.map((c) => _similarity(sc, c)));
      if (s > bestScore) { best = f.key; bestScore = s; }
    });
    map[sc] = best;
  });
  return map;
};

// ─── Row-level validator ────────────────────────────────────────────────
function validateRows(rows, fields, colMap) {
  const errors = [];
  const seenIds = new Map();  // for `unique` fields
  const reverseMap = Object.fromEntries(
    Object.entries(colMap).filter(([, v]) => v).map(([k, v]) => [v, k])
  );
  rows.forEach((row, ri) => {
    fields.forEach((f) => {
      const srcCol = reverseMap[f.key];
      const raw = srcCol ? row[srcCol] : undefined;
      const val = raw == null || raw === '' ? null : raw;

      // 1. Required
      if (f.required && (val == null || String(val).trim() === '')) {
        errors.push({ row: ri + 2, col: srcCol || f.label, field: f.key, severity: 'error',
          message: `${f.label} is required`, suggestion: 'Provide a value in this cell' });
        return;
      }
      if (val == null) return;

      // 2. Enum
      if (f.enum && !f.enum.map(String).map((v) => v.toLowerCase()).includes(String(val).toLowerCase())) {
        errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'error',
          message: `${f.label} = "${val}" is not one of ${f.enum.join(', ')}`,
          suggestion: `Change to one of: ${f.enum.slice(0, 5).join(', ')}` });
      }
      // 3. Regex
      if (f.pattern && !new RegExp(f.pattern).test(String(val))) {
        errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'error',
          message: `${f.label} = "${val}" doesn't match required pattern`,
          suggestion: f.patternHint || 'Check the expected format' });
      }
      // 4. Type — number / date
      if (f.type === 'number') {
        const n = Number(val);
        if (!Number.isFinite(n)) {
          errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'error',
            message: `${f.label} = "${val}" is not a valid number`, suggestion: 'Enter a numeric value' });
        } else if (f.min != null && n < f.min) {
          errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'warning',
            message: `${f.label} = ${n} is below the minimum of ${f.min}`, suggestion: `Increase to ≥ ${f.min}` });
        } else if (f.max != null && n > f.max) {
          errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'warning',
            message: `${f.label} = ${n} is above the maximum of ${f.max}`, suggestion: `Reduce to ≤ ${f.max}` });
        }
      }
      if (f.type === 'date') {
        // Accept Excel date-serials, ISO strings, and DD-MM-YYYY / MM/DD/YYYY forms.
        const isSerial = typeof val === 'number' && val > 20000 && val < 80000;
        const parsed = isSerial ? XLSX.SSF.parse_date_code(val) : new Date(val);
        const ok = isSerial ? !!parsed : !Number.isNaN(parsed.getTime());
        if (!ok) {
          errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'error',
            message: `${f.label} = "${val}" is not a valid date`, suggestion: 'Use YYYY-MM-DD or a real Excel date' });
        }
      }
      // 5. Unique
      if (f.unique) {
        const key = String(val).trim().toLowerCase();
        const prev = seenIds.get(f.key + ':' + key);
        if (prev != null) {
          errors.push({ row: ri + 2, col: srcCol, field: f.key, severity: 'error',
            message: `${f.label} = "${val}" duplicates row ${prev}`, suggestion: 'Use a unique identifier' });
        } else {
          seenIds.set(f.key + ':' + key, ri + 2);
        }
      }
    });
  });
  return errors;
}

// ─── Errors XLSX — highlighted red for offending cells ─────────────────
function buildErrorsXlsx(rows, sourceCols, errors, moduleLabel) {
  const wb = XLSX.utils.book_new();
  // Header row + all data rows + a "__error" column at the end
  const withNote = rows.map((r, ri) => {
    const rowErrs = errors.filter((e) => e.row === ri + 2);
    return { ...r, __errors: rowErrs.map((e) => `${e.col || e.field}: ${e.message}`).join(' | ') };
  });
  const headers = [...sourceCols, '__errors'];
  const ws = XLSX.utils.json_to_sheet(withNote, { header: headers });
  // Colour offending cells red using cell styles (best-effort — SheetJS
  // community build ignores styles on write, but many corp Excel setups
  // preserve them via ExcelJS re-open).  We add a `!red` marker at least.
  errors.forEach((e) => {
    const colIdx = headers.indexOf(e.col);
    if (colIdx < 0) return;
    const addr = XLSX.utils.encode_cell({ r: e.row - 1, c: colIdx });
    if (!ws[addr]) ws[addr] = { v: '', t: 's' };
    ws[addr].s = { fill: { fgColor: { rgb: 'FFCCCC' } }, font: { color: { rgb: '9B1C1C' } } };
    if (!ws[addr].c) ws[addr].c = [];
    ws[addr].c.push({ a: 'Panorix', t: e.message });
  });
  XLSX.utils.book_append_sheet(wb, ws, 'Errors');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${moduleLabel.replace(/\s+/g, '_')}_errors_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
}

export default function GuidedImportWizard({
  open,
  onOpenChange,
  moduleLabel,
  projectId,
  fields = [],
  sampleRows = [],
  templateEndpoint,
  importEndpoint,
  exportEndpoint,               // NEW — pulls the current dataset already in the system
  exportLabel = 'Download Current Data',
  extraTemplates = [],           // NEW — [{label, endpoint, description}]
  matrixImports = [],            // NEW — [{label, importEndpoint, description}] for diff-preview matrix uploads
  onImportComplete,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [sourceCols, setSourceCols] = useState([]);
  const [colMap, setColMap] = useState({});
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  // Matrix Diff Preview state (nested flow for stage-gate matrix uploads)
  const [matrixFlow, setMatrixFlow] = useState(null);   // {label, importEndpoint, file, diff, diffCount, updatedEntries, updatedGates, errors, applying}
  const matrixFileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setRows([]); setSourceCols([]);
      setColMap({}); setErrors([]); setImportResult(null); setBusy(false);
      setMatrixFlow(null);
    }
  }, [open]);

  // ── Matrix Import (Diff Preview) — nested flow ─────────────────────
  const openMatrixFlow = useCallback((tpl) => {
    setMatrixFlow({ label: tpl.label, importEndpoint: tpl.importEndpoint,
      file: null, diff: [], diffCount: 0,
      updatedEntries: 0, updatedGates: 0, errors: [], applying: false, phase: 'pick' });
    // trigger native file picker on next tick
    setTimeout(() => matrixFileRef.current?.click(), 30);
  }, []);

  const handleMatrixPick = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f || !matrixFlow) return;
    setMatrixFlow((s) => ({ ...s, file: f, phase: 'previewing', applying: true }));
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post(`${matrixFlow.importEndpoint}?preview=true`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setMatrixFlow((s) => ({ ...s,
        diff: data.diff || [], diffCount: data.diffCount || (data.diff || []).length,
        updatedEntries: data.updated_entries || 0,
        updatedGates: data.updated_gates || 0,
        errors: data.errors || [],
        applying: false, phase: 'preview' }));
      if ((data.diffCount || (data.diff || []).length) === 0) {
        toast.info('No cell changes detected — file matches current data.');
      } else {
        toast.success(`Diff preview: ${data.diffCount} cell changes across ${data.updated_entries} MCI(s).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Matrix preview failed');
      setMatrixFlow((s) => ({ ...s, applying: false, phase: 'pick' }));
    }
    // reset input so the same file can be re-picked
    if (matrixFileRef.current) matrixFileRef.current.value = '';
  }, [matrixFlow]);

  const applyMatrixConfirm = useCallback(async () => {
    if (!matrixFlow?.file) return;
    setMatrixFlow((s) => ({ ...s, applying: true }));
    try {
      const fd = new FormData();
      fd.append('file', matrixFlow.file);
      const { data } = await api.post(matrixFlow.importEndpoint, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Matrix applied · ${data.updated_gates || 0} gate(s) across ${data.updated_entries || 0} MCI(s)${data.healed ? ` · healed ${data.healed}` : ''}`);
      onImportComplete?.(data);
      setMatrixFlow(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Matrix apply failed');
      setMatrixFlow((s) => ({ ...s, applying: false }));
    }
  }, [matrixFlow, onImportComplete]);

  // ── Step 1 — Download template from the module's backend endpoint ───
  const handleDownloadTemplate = useCallback(async () => {
    if (!templateEndpoint) {
      // Fallback: generate a template client-side from `fields` + `sampleRows`
      const header = fields.map((f) => f.label);
      const example = sampleRows.length
        ? sampleRows
        : [Object.fromEntries(fields.map((f) => [f.label, f.example ?? '']))];
      const ws = XLSX.utils.json_to_sheet(example, { header });
      // Freeze header + widen columns
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
      ws['!cols'] = header.map((h) => ({ wch: Math.max(14, h.length + 2) }));
      // Header row hint row (row 2) with type/required
      const hint = Object.fromEntries(fields.map((f) => [
        f.label,
        [
          f.required ? 'REQUIRED' : 'optional',
          f.type ? f.type : null,
          f.enum ? `enum: ${f.enum.slice(0, 4).join('|')}${f.enum.length > 4 ? '…' : ''}` : null,
          f.example ? `e.g. ${f.example}` : null,
        ].filter(Boolean).join(' · '),
      ]));
      XLSX.utils.sheet_add_json(ws, [hint], { skipHeader: true, origin: -1 });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, moduleLabel.slice(0, 30));
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${moduleLabel.replace(/\s+/g, '_')}_Template.xlsx`;
      link.click();
      toast.success('Template downloaded');
      return;
    }
    try {
      const r = await api.get(templateEndpoint, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${moduleLabel.replace(/\s+/g, '_')}_Template.xlsx`;
      link.click();
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  }, [fields, sampleRows, moduleLabel, templateEndpoint]);

  // Download the current dataset already stored in the system (filled xlsx).
  const handleDownloadCurrent = useCallback(async () => {
    if (!exportEndpoint) return;
    try {
      const r = await api.get(exportEndpoint, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${moduleLabel.replace(/\s+/g, '_')}_Current.xlsx`;
      link.click();
      toast.success('Current dataset downloaded');
    } catch (e) {
      toast.error('Failed to download current dataset');
    }
  }, [exportEndpoint, moduleLabel]);

  // Download any extra template (e.g. Stage-Gate matrix)
  const handleDownloadExtra = useCallback(async (endpoint, label) => {
    try {
      const r = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${label.replace(/\s+/g, '_')}.xlsx`;
      link.click();
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error(`Failed to download ${label}`);
    }
  }, []);

  // ── Step 2 — Parse the picked file ───────────────────────────────────
  const handlePickFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) {
      toast.error('Please pick an .xlsx / .xls / .csv file'); return;
    }
    setFile(f);
    setBusy(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      // Read the first row as header, then json_to_sheet-style records.
      const arr = XLSX.utils.sheet_to_json(ws, { defval: null });
      if (!arr.length) {
        toast.warning('The file has no data rows'); setBusy(false); return;
      }
      const cols = Object.keys(arr[0] || {});
      setSourceCols(cols);
      setRows(arr);
      setColMap(_autoMap(cols, fields));
      setStep(2);
      toast.success(`Parsed ${arr.length} rows · ${cols.length} columns`);
    } catch (e) {
      toast.error('Failed to parse file — is it a valid Excel/CSV?');
    } finally { setBusy(false); }
  }, [fields]);

  // ── Step 3 — Validate ────────────────────────────────────────────────
  const runValidation = useCallback(() => {
    const errs = validateRows(rows, fields, colMap);
    setErrors(errs);
    setStep(3);
    if (errs.length === 0) toast.success('All rows valid — ready to import');
    else toast.warning(`${errs.length} issue${errs.length > 1 ? 's' : ''} found — review below`);
  }, [rows, fields, colMap]);

  // ── Step 4 — Confirm & upload to backend ─────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!importEndpoint || !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(importEndpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      setStep(4);
      const created = data.created ?? data.imported ?? data.stats?.tasks ?? 0;
      if (created > 0) toast.success(`Imported ${created} rows`);
      if (data.skipped) toast.warning(`${data.skipped} rows skipped`);
      onImportComplete?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
      setImportResult({ error: e?.response?.data?.detail || e.message });
      setStep(4);
    } finally { setBusy(false); }
  }, [file, importEndpoint, onImportComplete]);

  // ─── Derived — mapping completeness ─────────────────────────────────
  const missingRequired = useMemo(() => {
    const mapped = new Set(Object.values(colMap).filter(Boolean));
    return fields.filter((f) => f.required && !mapped.has(f.key));
  }, [colMap, fields]);
  const errorCount = errors.filter((e) => e.severity === 'error').length;
  const warningCount = errors.filter((e) => e.severity === 'warning').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="guided-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
            {moduleLabel} — Guided Import
          </DialogTitle>
          <DialogDescription>
            4 steps: <b>Template</b> → <b>Upload &amp; Map</b> → <b>Validate</b> → <b>Confirm</b>. Cell-level errors are
            shown inline and can be downloaded as a highlighted errors sheet.
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex items-center gap-2 py-2">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                step >= n ? 'text-white' : ''
              }`} style={{
                backgroundColor: step >= n ? 'var(--accent-primary)' : 'var(--card-bg-alt)',
                color: step >= n ? '#fff' : 'var(--text-muted)'
              }}>
                {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              <span className="text-xs font-medium" style={{ color: step >= n ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {['Template', 'Upload & Map', 'Validate', 'Confirm'][n - 1]}
              </span>
              {n < 4 && <div className="flex-1 h-[1px]" style={{ backgroundColor: 'var(--border-color)' }} />}
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-3">
          {/* ── STEP 1 — TEMPLATE ─────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-1">
              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>1️⃣ Grab the template</h4>
                <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                  The template carries the exact column headers, a hint row (REQUIRED / optional / enum options) and a sample record so you can never guess the format. Fill it in Excel and drop it back in step 2.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleDownloadTemplate} data-testid="wizard-download-template">
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </Button>
                  {exportEndpoint && (
                    <Button variant="outline" onClick={handleDownloadCurrent} data-testid="wizard-download-current">
                      <FileDown className="w-4 h-4 mr-2" /> {exportLabel}
                    </Button>
                  )}
                  {matrixImports.map((tpl) => (
                    <Button key={`mi-${tpl.label}`} variant="outline"
                      onClick={() => openMatrixFlow(tpl)}
                      data-testid={`wizard-matrix-import-${tpl.label.replace(/\s+/g, '-').toLowerCase()}`}
                      title="Upload this matrix and preview a diff before saving.">
                      <Upload className="w-4 h-4 mr-2" /> {tpl.label}
                    </Button>
                  ))}
                  {extraTemplates.map((tpl) => (
                    <Button key={tpl.label} variant="outline"
                      onClick={() => handleDownloadExtra(tpl.endpoint, tpl.label)}
                      data-testid={`wizard-extra-template-${tpl.label.replace(/\s+/g, '-').toLowerCase()}`}>
                      <Download className="w-4 h-4 mr-2" /> {tpl.label}
                    </Button>
                  ))}
                </div>
                {(extraTemplates.length > 0 || matrixImports.length > 0) && (
                  <div className="mt-3 text-[11px] space-y-1" style={{ color: 'var(--text-muted)' }}>
                    {matrixImports.map((tpl) => (
                      <div key={`mid-${tpl.label}`}>
                        <b style={{ color: 'var(--text-primary)' }}>{tpl.label}:</b> {tpl.description}
                      </div>
                    ))}
                    {extraTemplates.map((tpl) => (
                      <div key={tpl.label}>
                        <b style={{ color: 'var(--text-primary)' }}>{tpl.label}:</b> {tpl.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Field catalogue</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {fields.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]"
                        style={{
                          background: f.required ? '#1E4E8C' : 'transparent',
                          color:      f.required ? '#ffffff' : 'var(--text-muted)',
                          border:     f.required ? '1px solid #1E4E8C' : '1px solid var(--border-color)',
                        }}>
                        {f.required ? 'REQ' : 'OPT'}
                      </span>
                      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{f.label}</span>
                      {f.type && <span style={{ color: 'var(--text-muted)' }}>· {f.type}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => fileRef.current?.click()} data-testid="wizard-pick-file">
                  <Upload className="w-4 h-4 mr-2" /> I have the file — Upload
                </Button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handlePickFile} className="hidden" data-testid="wizard-file-input" />
              </div>
            </div>
          )}

          {/* ── STEP 2 — UPLOAD & MAP ─────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-2">
              <div className="p-3 rounded-lg border flex items-center justify-between" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
                <div>
                  <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{file?.name}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{rows.length} rows · {sourceCols.length} columns detected</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Change file
                </Button>
              </div>
              <div className="rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-xs">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr>
                      <th className="text-left p-2" style={{ color: 'var(--text-muted)' }}>Source column</th>
                      <th className="text-left p-2" style={{ color: 'var(--text-muted)' }}>Sample value</th>
                      <th className="text-left p-2" style={{ color: 'var(--text-muted)' }}>Panorix field</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceCols.map((sc) => (
                      <tr key={sc} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-2 font-medium" style={{ color: 'var(--text-primary)' }}>{sc}</td>
                        <td className="p-2 truncate max-w-[160px]" style={{ color: 'var(--text-muted)' }}>{String(rows[0]?.[sc] ?? '')}</td>
                        <td className="p-2">
                          <Select value={colMap[sc] || '__none__'} onValueChange={(v) => setColMap({ ...colMap, [sc]: v === '__none__' ? null : v })}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`col-map-${sc}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Ignore —</SelectItem>
                              {fields.map((f) => (
                                <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {missingRequired.length > 0 && (
                <div className="p-3 rounded-lg border text-xs flex items-start gap-2" style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)', color: 'var(--text-primary)' }}>
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                  <div>
                    <b>{missingRequired.length} required field(s) not mapped:</b>{' '}
                    {missingRequired.map((f) => f.label).join(', ')}. Map them before continuing.
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={runValidation} disabled={missingRequired.length > 0} data-testid="wizard-validate">
                  Validate <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 3 — VALIDATE ─────────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'rgba(16,185,129,0.08)' }}>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4" style={{ color: '#10B981' }} /><span style={{ color: 'var(--text-muted)' }}>Valid rows</span></div>
                  <div className="text-xl font-bold" style={{ color: '#10B981' }}>{rows.length - new Set(errors.filter(e => e.severity === 'error').map(e => e.row)).size}</div>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'rgba(245,158,11,0.08)' }}>
                  <div className="flex items-center gap-1"><AlertTriangle className="w-4 h-4" style={{ color: '#F59E0B' }} /><span style={{ color: 'var(--text-muted)' }}>Warnings</span></div>
                  <div className="text-xl font-bold" style={{ color: '#F59E0B' }}>{warningCount}</div>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'rgba(239,68,68,0.08)' }}>
                  <div className="flex items-center gap-1"><XCircle className="w-4 h-4" style={{ color: '#EF4444' }} /><span style={{ color: 'var(--text-muted)' }}>Errors</span></div>
                  <div className="text-xl font-bold" style={{ color: '#EF4444' }}>{errorCount}</div>
                </div>
              </div>
              {errors.length > 0 ? (
                <>
                  <div className="rounded-lg border max-h-[280px] overflow-auto" style={{ borderColor: 'var(--border-color)' }}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                        <tr>
                          <th className="text-left p-2">Row</th>
                          <th className="text-left p-2">Column</th>
                          <th className="text-left p-2">Severity</th>
                          <th className="text-left p-2">Message</th>
                          <th className="text-left p-2">Suggested fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errors.slice(0, 200).map((e, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                            <td className="p-2 mono">{e.row}</td>
                            <td className="p-2 font-medium">{e.col}</td>
                            <td className="p-2">
                              <Badge variant={e.severity === 'error' ? 'destructive' : 'outline'} className="text-[10px]">{e.severity}</Badge>
                            </td>
                            <td className="p-2" style={{ color: 'var(--text-primary)' }}>{e.message}</td>
                            <td className="p-2" style={{ color: 'var(--text-muted)' }}>{e.suggestion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {errors.length > 200 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Showing first 200 of {errors.length} issues. Download the errors sheet for the full list.</div>}
                  <Button variant="outline" size="sm" onClick={() => buildErrorsXlsx(rows, sourceCols, errors, moduleLabel)} data-testid="wizard-download-errors">
                    <FileDown className="w-4 h-4 mr-2" /> Download errors.xlsx (cells highlighted red)
                  </Button>
                </>
              ) : (
                <div className="p-6 text-center rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'rgba(16,185,129,0.05)' }}>
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2" style={{ color: '#10B981' }} />
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>All rows are valid</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Ready to import into the {moduleLabel} module.</div>
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" /> Back to mapping</Button>
                <Button onClick={handleConfirm} disabled={errorCount > 0 || busy} data-testid="wizard-confirm-import">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import {rows.length - new Set(errors.filter(e => e.severity === 'error').map(e => e.row)).size} rows
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 4 — RESULT ─────────────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-4">
              {importResult?.error ? (
                <div className="p-4 rounded-lg border" style={{ borderColor: '#EF4444', background: 'rgba(239,68,68,0.08)', color: 'var(--text-primary)' }}>
                  <div className="flex items-center gap-2 mb-1"><XCircle className="w-5 h-5" style={{ color: '#EF4444' }} /><b>Import failed</b></div>
                  <div className="text-sm">{importResult.error}</div>
                </div>
              ) : (
                <div className="p-6 text-center rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'rgba(16,185,129,0.05)' }}>
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2" style={{ color: '#10B981' }} />
                  <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Import complete</div>
                  <div className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                    Created <b>{importResult?.created ?? importResult?.imported ?? 0}</b>
                    {importResult?.updated ? <>, updated <b>{importResult.updated}</b></> : null}
                    {importResult?.skipped ? <>, skipped <b>{importResult.skipped}</b></> : null}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => onOpenChange(false)}>Close</Button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden matrix file input (Diff Preview flow) */}
        <input ref={matrixFileRef} type="file" accept=".xlsx,.xls,.csv"
          onChange={handleMatrixPick} className="hidden"
          data-testid="wizard-matrix-file-input" />

        {/* Matrix Diff Preview overlay */}
        {matrixFlow && matrixFlow.phase !== 'pick' && (
          <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center p-4"
            onClick={() => !matrixFlow.applying && setMatrixFlow(null)}
            data-testid="matrix-diff-overlay">
            <div className="w-full max-w-3xl max-h-[80vh] flex flex-col rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                <div>
                  <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {matrixFlow.label} — Diff Preview
                  </div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {matrixFlow.diffCount} cell change(s) across {matrixFlow.updatedEntries} MCI(s)
                    {matrixFlow.errors?.length ? ` · ${matrixFlow.errors.length} error(s)` : ''}
                  </div>
                </div>
                {!matrixFlow.applying && (
                  <button onClick={() => setMatrixFlow(null)} data-testid="matrix-diff-close">
                    <XCircle className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                  </button>
                )}
              </div>
              <div className="flex-1 min-h-0 overflow-auto p-3">
                {matrixFlow.applying && matrixFlow.phase === 'previewing' && (
                  <div className="flex items-center gap-2 justify-center py-10 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" /> Computing diff…
                  </div>
                )}
                {matrixFlow.phase === 'preview' && matrixFlow.diff.length === 0 && (
                  <div className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    No changes detected — the uploaded matrix matches the current data.
                  </div>
                )}
                {matrixFlow.phase === 'preview' && matrixFlow.diff.length > 0 && (
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                      <tr>
                        <th className="text-left p-1.5">MCI</th>
                        <th className="text-left p-1.5">Phase</th>
                        <th className="text-left p-1.5">Gate</th>
                        <th className="text-left p-1.5">Field</th>
                        <th className="text-left p-1.5">Before</th>
                        <th className="text-left p-1.5">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixFlow.diff.slice(0, 300).map((d, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}
                          data-testid={`matrix-diff-row-${i}`}>
                          <td className="p-1.5 font-mono">{d.mciLineNumber}</td>
                          <td className="p-1.5">{d.phase}</td>
                          <td className="p-1.5">{d.stageGateName || d.stageGateId}</td>
                          <td className="p-1.5">{d.field}</td>
                          <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{String(d.before || '—').slice(0, 20)}</td>
                          <td className="p-1.5" style={{ color: 'var(--accent-primary)' }}>{String(d.after || '—').slice(0, 20)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {matrixFlow.errors && matrixFlow.errors.length > 0 && (
                  <div className="mt-2 p-2 rounded border text-[11px]"
                    style={{ borderColor: '#EF4444', background: 'rgba(239,68,68,0.08)' }}>
                    <b style={{ color: '#EF4444' }}>Errors ({matrixFlow.errors.length}):</b>
                    <ul className="mt-1 space-y-0.5 list-disc pl-5" style={{ color: 'var(--text-primary)' }}>
                      {matrixFlow.errors.slice(0, 20).map((er, i) => <li key={i}>{String(er)}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <div className="p-3 border-t flex justify-between items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <CheckCircle2 className="w-3 h-3 inline mr-0.5" style={{ color: '#10B981' }} />
                  Nothing has been saved yet — this is a preview only.
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setMatrixFlow(null)}
                    disabled={matrixFlow.applying} data-testid="matrix-diff-cancel">Cancel</Button>
                  <Button size="sm" onClick={applyMatrixConfirm}
                    disabled={matrixFlow.applying || matrixFlow.diff.length === 0}
                    data-testid="matrix-diff-apply">
                    {matrixFlow.applying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Apply {matrixFlow.diffCount} change{matrixFlow.diffCount === 1 ? '' : 's'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}